import { Calendar, ChevronRight } from 'react-feather';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import {
  GetDashboardStatsQuery as GetDashboardStats,
} from '__generated__/graphql';

import { LinkBlockContainer } from '../../common/UI/Nav';
import { resizedImageUrl } from '../../common/helpers';
import DateRange from '../../common/Common/DateRange';
import GetDashboardStatsQuery from './GetDashboardStatsQuery';
import TotalRegistrationsGraph from './TotalRegistrationsGraph';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';
import useUrlState from '../../common/useUrlState';

export interface DashboardPageProps {
  startDate?: string;
}

const DashboardPage = ({ startDate: defaultStartDate }: DashboardPageProps) => {
  const { formatDate, today } = useLocale();
  const history = useHistory();
  const project = useProject();

  /**
   * Handle legacy Dashboard URLs with ?event= params
   */
  const [urlState] = useUrlState({
    parse: (params, { string, integer }) => ({
      ...params,
      event: string(params.event),
      page: integer(params.page),
    }),
  });

  /**
   * The start of today in the project's timezone converted to UTC
   */
  const since = defaultStartDate || formatDate(
    today(),
    { format: 'internal_date_time', timezone: null },
  );

  const { data, loading } = useQuery(
    GetDashboardStatsQuery,
    {
      variables: {
        project_id: project.id,
        since,
        page: urlState.page || 1,
      },
      pollInterval: 30000,
    },
  );

  useEffect(() => {
    if (data?.project.events.total === 1) {
      history.replace(`/${project.id}/dashboard/${data.project.events.data[0].id}`);
    } else if (urlState.event) {
      // Legacy redirect.
      history.replace(`/${project.id}/dashboard/${urlState.event}`);
    }
  }, [history, data, project.id, urlState.event]);

  if (!data || data.project.events.total === 1) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <TotalRegistrationsGraph data={data.project.analytics.monthly_registration_counts} />
      <EventsOverview events={data.project.events} loading={loading} />
    </>
  );
};

interface EventsOverviewProps {
  events: GetDashboardStats['project']['events'];
  loading?: boolean;
}

const EventsOverview = ({ events, loading }: EventsOverviewProps) => {
  const project = useProject();
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const device = useMediaDevice();

  return (
    <EventsOverviewContainer sc={{ hasPanels: true, loading }}>
      <UI.GridContainer sc={{ columns: '44px 9fr 4fr 3fr 150px', gutter: 0.75, alignVertical: 'center', py: 3 }}>
        <UI.H4 style={{ gridColumn: '1 / span 2' }}>{t('events')}</UI.H4>
        {!device.isSmall && <UI.Legend>{t('registrations')}</UI.Legend>}
        {!device.isSmall && <UI.Legend>{t('today')}</UI.Legend>}
      </UI.GridContainer>
      {events.data.map((event) => (
        <UI.LinkBlock to={`/${project.id}/dashboard/${event.id}`} sc={{ py: 2 }} title={event.title} key={event.id}>
          <UI.GridContainer
            sc={{ columns: ['44px 1fr 14px', '44px 9fr 4fr 3fr 150px'], gutter: 0.75, alignVertical: 'center' }}
          >
            <UI.Div>
              <UI.ProfilePic
                sc={{ size: 44, background: event.logo ? 'white' : undefined }}
              >
                {event.logo && (
                <img
                  src={resizedImageUrl(event.logo.url, 300, 'webp')}
                  alt={event.title}
                />
                )}
                {!event.logo && (
                <UI.Span><UI.Span>{event.title.charAt(0)}</UI.Span></UI.Span>
                )}
              </UI.ProfilePic>
            </UI.Div>
            <UI.Div>
              <UI.H4>
                {event.title}
              </UI.H4>
              <UI.Div sc={{ small: true, muted: true }}>
                <UI.Icon>
                  <Calendar />
                </UI.Icon>
                {' '}
                <DateRange start={event.start_date} end={event.end_date} />
              </UI.Div>
            </UI.Div>
            {!device.isSmall && (
              <UI.Div>
                {formatInteger(event.registrations_count)}
              </UI.Div>
            )}
            {!device.isSmall && (
              <UI.Div>
                <UI.Label
                  sc={{
                    background: event.new_registrations_count > 0 ? 'primary.25' : undefined,
                    color: event.new_registrations_count > 0 ? 'primary.700' : 'gray.500',
                  }}
                  style={{ fontWeight: 500 }}
                >
                  <UI.Small>
                    {'+ '}
                    {formatInteger(event.new_registrations_count)}
                  </UI.Small>
                </UI.Label>
              </UI.Div>
            )}
            <UI.Div sc={{ textAlign: 'right', small: true }}>
              {!device.isSmall && ` ${t('view_statistics')}`}
              <UI.Icon style={{ marginBottom: 1.5 }}>
                <ChevronRight />
              </UI.Icon>
            </UI.Div>
          </UI.GridContainer>
        </UI.LinkBlock>
      ))}
      {events.last_page > 1 && (
        <UI.Div>
          <UI.Pager pageCount={events.last_page} size="sm" />
        </UI.Div>
      )}
    </EventsOverviewContainer>
  );
};

const EventsOverviewContainer = styled(UI.Card)`
  ${({ theme }) => css`
    ${LinkBlockContainer} {
        & > ${UI.GridContainer} > :last-child {
          transition: all 0.15s ease-in-out;
          color: ${theme.colors.gray[500]};
        }

        &:hover > ${UI.GridContainer} > :last-child {
          margin-right: -3px;
          color: ${theme.colors.gray[800]};
        }
      }
  `}
`;

export default DashboardPage;
